import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../services/api.ts';

type StoryStatusLike = {
  current_step?: string;
};

interface RawSceneImage {
  id?: number;
  scene_number: number;
  description?: string;
  image_prompt?: string;  // For backward compatibility
  starting_frame_url?: string;
  end_frame_url?: string;
  start_frame_prompt?: string;
  end_frame_prompt?: string;
  status?: 'generating' | 'completed';
}

export function useSceneImagesQuery(
  storyId: number | null,
  storyStatus?: StoryStatusLike | null
) {
  return useQuery<RawSceneImage[]>({
    queryKey: ['scene-images', storyId],
    enabled: !!storyId,
    queryFn: async () => {
      const res = await apiGet<RawSceneImage[]>(`/runs/${storyId}/scene-images`);
      if (res.error) throw new Error(res.error);
      return res.data ?? [];
    },
    // Only poll aggressively while we're on the "scenes" step and items are generating.
    refetchInterval: (q) => {
      const data = (q.state.data as RawSceneImage[] | undefined) ?? [];
      const onScenesStep = storyStatus?.current_step === 'scenes';
      const anyGenerating = data.some(s => s.status !== 'completed' || !s.starting_frame_url || !s.end_frame_url);
      return onScenesStep && anyGenerating ? 2000 : false;
    },
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev, // keeps UI stable between refetches
    staleTime: 60_000,               // good cache for completed scenes
  });
}
